import React, { useEffect, useState } from 'react';
import { settingsApi, emailAccountsApi } from '../api';
import { toast } from 'sonner';
import {
  Save, Eye, EyeOff, Loader2, Key, Mail, Plus, Trash2, Zap,
  CheckCircle, AlertTriangle, User, Settings2, Shield, X
} from 'lucide-react';

export default function Settings() {
  const [settings, setSettings] = useState({ from_name: '', validto_key: '', apollo_key: '' });
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [show, setShow] = useState({});
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPerMin, setNewPerMin] = useState(2);
  const [newPerDay, setNewPerDay] = useState(300);
  const [adding, setAdding] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [testing, setTesting] = useState(null);
  const [healthCheck, setHealthCheck] = useState(null);
  const [healthLoading, setHealthLoading] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([settingsApi.get(), emailAccountsApi.getAll()]);
      setSettings(s);
      setAccounts(a);
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try { await settingsApi.update(settings); toast.success('Settings saved'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleAddAccount = async () => {
    if (!newEmail.trim() || !newPassword.trim()) { toast.error('Email and password required'); return; }
    setAdding(true);
    try {
      await emailAccountsApi.add({ email: newEmail, app_password: newPassword, per_minute_limit: newPerMin, per_day_limit: newPerDay });
      toast.success('Email account added');
      setNewEmail(''); setNewPassword(''); setNewPerMin(2); setNewPerDay(300);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add'); }
    finally { setAdding(false); }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Remove this email account?')) return;
    try { await emailAccountsApi.delete(id); toast.success('Account removed'); fetchAll(); }
    catch { toast.error('Failed to delete'); }
  };

  const handleTestAccount = async (id) => {
    setTesting(id);
    try { const res = await emailAccountsApi.test(id); toast[res.success ? 'success' : 'error'](res.message); }
    catch { toast.error('Test failed'); }
    finally { setTesting(null); }
  };

  const handleToggleStatus = async (id, current) => {
    try { await emailAccountsApi.update(id, { status: current === 'active' ? 'paused' : 'active' }); fetchAll(); }
    catch { toast.error('Failed to update'); }
  };

  const handleHealthCheck = async (id) => {
    setHealthLoading(id);
    try {
      const res = await emailAccountsApi.health(id);
      setHealthCheck(res);
    } catch { toast.error('Health check failed'); }
    finally { setHealthLoading(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#1A73E8]" /></div>;

  const StatusIcon = ({ status }) => {
    if (status === 'pass') return <CheckCircle className="w-4 h-4 text-[#1E8E3E]" />;
    if (status === 'warn') return <AlertTriangle className="w-4 h-4 text-[#F9AB00]" />;
    return <AlertTriangle className="w-4 h-4 text-[#D93025]" />;
  };

  return (
    <div className="max-w-3xl" data-testid="outreach-settings">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-[#202124]">Settings</h1>
        <p className="text-sm text-[#5F6368]">Manage email accounts, API keys, and email health</p>
      </div>

      {/* General Settings */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-5">
        <h3 className="text-sm font-semibold text-[#202124] mb-4 flex items-center gap-2"><Settings2 className="w-4 h-4 text-[#1A73E8]" /> General Settings</h3>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-[#5F6368] font-medium block mb-1">From Name</label>
            <div className="relative">
              <User className="w-4 h-4 text-[#9AA0A6] absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={settings.from_name} onChange={(e) => setSettings(prev => ({ ...prev, from_name: e.target.value }))}
                placeholder="StartEase" className="w-full bg-[#F1F3F4] border border-transparent rounded-lg pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:bg-white focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20" data-testid="settings-from-name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-[#5F6368] font-medium block mb-1">ValidTo API Key</label>
              <div className="relative">
                <Key className="w-4 h-4 text-[#9AA0A6] absolute left-3 top-1/2 -translate-y-1/2" />
                <input type={show.mv ? 'text' : 'password'} value={settings.validto_key}
                  onChange={(e) => setSettings(prev => ({ ...prev, validto_key: e.target.value }))}
                  className="w-full bg-[#F1F3F4] border border-transparent rounded-lg pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:bg-white focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20" data-testid="settings-validto-key" />
                <button onClick={() => setShow(p => ({ ...p, mv: !p.mv }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA0A6] hover:text-[#5F6368]">
                  {show.mv ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm text-[#5F6368] font-medium block mb-1">Apollo.io API Key</label>
              <div className="relative">
                <Key className="w-4 h-4 text-[#9AA0A6] absolute left-3 top-1/2 -translate-y-1/2" />
                <input type={show.apollo ? 'text' : 'password'} value={settings.apollo_key}
                  onChange={(e) => setSettings(prev => ({ ...prev, apollo_key: e.target.value }))}
                  className="w-full bg-[#F1F3F4] border border-transparent rounded-lg pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:bg-white focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20" data-testid="settings-apollo-key" />
                <button onClick={() => setShow(p => ({ ...p, apollo: !p.apollo }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9AA0A6] hover:text-[#5F6368]">
                  {show.apollo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <button onClick={handleSaveSettings} disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#1A73E8] hover:bg-[#174EA6] text-white rounded-full text-sm font-semibold disabled:opacity-50 shadow-sm" data-testid="settings-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Settings
          </button>
        </div>
      </div>

      {/* Email Accounts */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 mb-5">
        <h3 className="text-sm font-semibold text-[#202124] mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#1E8E3E]" /> Email Accounts
          <span className="text-xs text-[#9AA0A6] font-normal ml-1">({accounts.length})</span>
        </h3>

        {accounts.length > 0 && (
          <div className="space-y-2 mb-5">
            {accounts.map((acc) => (
              <div key={acc.id} className={`bg-[#F8F9FA] border rounded-xl p-4 ${acc.status === 'active' ? 'border-[#E2E8F0]' : 'border-[#F9AB00]/30'}`} data-testid={`account-${acc.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${acc.status === 'active' ? 'bg-[#1E8E3E]' : 'bg-[#F9AB00]'}`} />
                    <div className="min-w-0">
                      <p className="text-sm text-[#202124] font-medium truncate">{acc.email}</p>
                      <div className="flex items-center gap-3 text-xs text-[#9AA0A6] mt-0.5">
                        <span>{acc.per_minute_limit}/min</span>
                        <span>{acc.per_day_limit}/day</span>
                        <span>Sent: <span className={acc.sent_today >= acc.per_day_limit ? 'text-[#D93025] font-semibold' : 'text-[#1E8E3E] font-semibold'}>{acc.sent_today || 0}</span></span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 ml-3">
                    <button onClick={() => handleHealthCheck(acc.id)} disabled={healthLoading === acc.id} title="Email Health Check"
                      className="p-1.5 text-[#5F6368] hover:text-[#1A73E8] hover:bg-[#E8F0FE] rounded-full">
                      {healthLoading === acc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                    </button>
                    <button onClick={() => handleTestAccount(acc.id)} disabled={testing === acc.id} title="Test SMTP"
                      className="p-1.5 text-[#5F6368] hover:text-[#1E8E3E] hover:bg-[#E6F4EA] rounded-full">
                      {testing === acc.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                    </button>
                    <button onClick={() => handleToggleStatus(acc.id, acc.status)}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-colors ${
                        acc.status === 'active' ? 'bg-[#E6F4EA] text-[#1E8E3E]' : 'bg-[#FEF7E0] text-[#F9AB00]'
                      }`}>
                      {acc.status === 'active' ? 'Active' : 'Paused'}
                    </button>
                    <button onClick={() => handleDeleteAccount(acc.id)}
                      className="p-1.5 text-[#9AA0A6] hover:text-[#D93025] hover:bg-[#FCE8E6] rounded-full">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add account */}
        <div className="bg-[#F8F9FA] border border-dashed border-[#E2E8F0] rounded-xl p-4">
          <p className="text-sm text-[#5F6368] mb-3 flex items-center gap-1.5 font-medium"><Plus className="w-4 h-4" /> Add Email Account</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs text-[#5F6368] font-medium block mb-1">Gmail Address</label>
              <input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="your@gmail.com"
                className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20" data-testid="new-account-email" />
            </div>
            <div>
              <label className="text-xs text-[#5F6368] font-medium block mb-1">App Password</label>
              <div className="relative">
                <input type={showNewPass ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="xxxx xxxx xxxx xxxx"
                  className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 pr-9 py-2 text-sm focus:outline-none focus:border-[#1A73E8] focus:ring-2 focus:ring-[#1A73E8]/20" data-testid="new-account-password" />
                <button onClick={() => setShowNewPass(!showNewPass)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9AA0A6] hover:text-[#5F6368]">
                  {showNewPass ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-[#5F6368] font-medium block mb-1">Per Minute Limit</label>
              <input type="number" min={1} max={30} value={newPerMin} onChange={(e) => setNewPerMin(parseInt(e.target.value) || 1)}
                className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1A73E8]" data-testid="new-account-per-min" />
            </div>
            <div>
              <label className="text-xs text-[#5F6368] font-medium block mb-1">Per Day Limit</label>
              <input type="number" min={1} max={2000} value={newPerDay} onChange={(e) => setNewPerDay(parseInt(e.target.value) || 1)}
                className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#1A73E8]" data-testid="new-account-per-day" />
            </div>
          </div>
          <button onClick={handleAddAccount} disabled={adding || !newEmail.trim() || !newPassword.trim()}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#1E8E3E] hover:bg-[#137333] text-white rounded-full text-sm font-medium disabled:opacity-50 shadow-sm" data-testid="add-account-btn">
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Account
          </button>
        </div>
      </div>

      {/* Health Check Results Modal */}
      {healthCheck && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setHealthCheck(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#F1F3F4]">
              <h3 className="text-base font-semibold text-[#202124] flex items-center gap-2"><Shield className="w-5 h-5 text-[#1A73E8]" /> Email Health: {healthCheck.domain}</h3>
              <button onClick={() => setHealthCheck(null)} className="p-1 hover:bg-[#F1F3F4] rounded-full"><X className="w-4 h-4 text-[#5F6368]" /></button>
            </div>
            <div className="p-6 space-y-4">
              {['mx', 'spf', 'dkim', 'dmarc'].map(key => {
                const item = healthCheck[key];
                if (!item) return null;
                return (
                  <div key={key} className={`p-4 rounded-xl border ${
                    item.status === 'pass' ? 'bg-[#E6F4EA] border-[#1E8E3E]/20' : item.status === 'warn' ? 'bg-[#FEF7E0] border-[#F9AB00]/20' : 'bg-[#FCE8E6] border-[#D93025]/20'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      <StatusIcon status={item.status} />
                      <span className="text-sm font-semibold text-[#202124] uppercase">{key}</span>
                      <span className={`text-xs font-semibold ml-auto ${
                        item.status === 'pass' ? 'text-[#1E8E3E]' : item.status === 'warn' ? 'text-[#F9AB00]' : 'text-[#D93025]'
                      }`}>{item.status.toUpperCase()}</span>
                    </div>
                    {item.records && key === 'mx' && (
                      <div className="mt-2 space-y-1">
                        {item.records.map((r, i) => (
                          <p key={i} className="text-xs text-[#5F6368] font-mono">Priority {r.priority}: {r.host}</p>
                        ))}
                      </div>
                    )}
                    {item.record && <p className="text-xs text-[#5F6368] font-mono mt-1 break-all">{item.record}</p>}
                    {item.message && <p className="text-xs text-[#5F6368] mt-1">{item.message}</p>}
                    {item.error && <p className="text-xs text-[#D93025] mt-1">{item.error}</p>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
